import { ed25519 } from '@noble/curves/ed25519.js';
import { concatBytes, utf8 } from './bytes.ts';

/**
 * Signed ticks (SPEC.md §5.2): every QR frame of a v3 stream carries one, so
 * freshness can be read from a SINGLE frame instead of a whole packet.
 *
 *   tick bytes (82, big-endian):  sid8 [8] | t u48 (Unix ms) | seq u32 | sig [64]
 *   sig = Ed25519(session key, "NIDT" | 0x01 | session id [16] | t u64 | seq u32)
 *
 * `sid8` is the first 8 bytes of the 16-byte session id (enough to tell
 * sessions apart in a frame); the signature covers the full id. The session's
 * public key travels in the signed packet (`session.pub`), so a tick verifies
 * only once its packet has been decoded and checked, and a tick of one session
 * never verifies for another.
 */
export const TICK_BYTES = 82;
export const SESSION_ID_BYTES = 16;
const TICK_MAGIC = utf8('NIDT');
const TICK_VERSION = 1;

export type Tick = { sid8: Uint8Array; t: number; seq: number; sig: Uint8Array };

export function tickMessage(sessionId: Uint8Array, t: number, seq: number): Uint8Array {
  if (sessionId.length !== SESSION_ID_BYTES) throw new Error('session id must be 16 bytes');
  if (!Number.isSafeInteger(t) || t < 0 || t >= 2 ** 48) throw new Error('tick time out of range');
  const tail = new Uint8Array(12);
  const dv = new DataView(tail.buffer);
  dv.setUint32(0, Math.floor(t / 2 ** 32));
  dv.setUint32(4, t >>> 0);
  dv.setUint32(8, seq >>> 0);
  return concatBytes(TICK_MAGIC, Uint8Array.of(TICK_VERSION), sessionId, tail);
}

/** Signs a tick with `sign` (the server's session key; node:crypto there, noble in tests). */
export function makeTick(sessionId: Uint8Array, t: number, seq: number, sign: (msg: Uint8Array) => Uint8Array): Tick {
  const sig = sign(tickMessage(sessionId, t, seq));
  if (sig.length !== 64) throw new Error('signature must be 64 bytes');
  return { sid8: sessionId.slice(0, 8), t, seq: seq >>> 0, sig };
}

export function encodeTick(k: Tick): Uint8Array {
  const out = new Uint8Array(TICK_BYTES);
  const dv = new DataView(out.buffer);
  out.set(k.sid8, 0);
  dv.setUint16(8, Math.floor(k.t / 2 ** 32));
  dv.setUint32(10, k.t >>> 0);
  dv.setUint32(14, k.seq >>> 0);
  out.set(k.sig, 18);
  return out;
}

export function decodeTick(b: Uint8Array): Tick {
  if (b.length !== TICK_BYTES) throw new Error('a tick is 82 bytes');
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  return { sid8: b.slice(0, 8), t: dv.getUint16(8) * 2 ** 32 + dv.getUint32(10), seq: dv.getUint32(14), sig: b.slice(18, 82) };
}

export const sameSid8 = (sid8: Uint8Array, sessionId: Uint8Array) => sid8.length === 8 && sid8.every((x, i) => x === sessionId[i]);

export function verifyTick(k: Tick, sessionId: Uint8Array, publicKey: Uint8Array): boolean {
  if (!sameSid8(k.sid8, sessionId)) return false;
  try {
    return ed25519.verify(k.sig, tickMessage(sessionId, k.t, k.seq), publicKey);
  } catch {
    return false;
  }
}

// ---- the tick rule ---------------------------------------------------------------------

export type TickOptions = {
  /** A tick older than this on the (corrected) phone clock is stale: a recording or a delayed copy. */
  maxTickAgeMs?: number;
  /** How far a tick may be in the corrected clock's future (the clock estimate's error). */
  maxFutureMs?: number;
  /** Fresh, verified ticks needed. */
  min?: number;
  /** ...seen over at least this long (so a burst of frames cannot count). */
  minSpanMs?: number;
  /** A tick whose age is this much above the youngest one's drifts: a paused or cut video (once is a network hiccup). */
  driftMs?: number;
  /** Drifted ticks that fail the rule. */
  driftFail?: number;
  /** The code's clock must run at this rate of the phone's (the median of pairwise rates): a sped-up or slowed video does not. */
  minRate?: number;
  maxRate?: number;
  /** Stale ticks that fail the rule (when there are at least as many stale as fresh ones). */
  staleFail?: number;
  /** Ticks older than this (seen time, from the newest) leave the window. */
  windowMs?: number;
};

export const TICK_DEFAULTS = { maxTickAgeMs: 2500, maxFutureMs: 1000, min: 6, minSpanMs: 1000, driftMs: 1500, driftFail: 3, minRate: 0.6, maxRate: 1.6, staleFail: 4, windowMs: 8000 } as const;

/** Without a clock sync the tick ages are only as good as the phone's clock: fall back to the packet's 30 s. */
export const UNSYNCED_MAX_TICK_AGE_MS = 30_000;

export type TickAnomaly = 'stale-tick' | 'out-of-order' | 'cadence' | 'clock';

export type TickState = {
  state: 'collecting' | 'live' | 'stale' | 'replay' | 'cadence';
  /** Fresh, verified ticks in the run. */
  fresh: number;
  stale: number;
  min: number;
  /** Ages (ms, corrected clock) of every verified tick in the run, in the order seen. */
  ages: number[];
  message: string;
  detail: string;
  anomaly?: TickAnomaly;
  /** The newest verified tick (sent with scan-seen as proof the scanner saw the code). */
  last?: Tick;
};

type Seen = { k: Tick; seenAt: number; ok?: boolean; again?: boolean };

/**
 * Collects the ticks of decoded frames (before their packet is known) and
 * judges them once the session id and key are known (the packet verified).
 * Seen times are on the phone's clock CORRECTED by the clock sync (clock.ts).
 */
export class TickTracker {
  readonly o: Required<TickOptions>;
  private seen: Seen[] = [];
  private seqs = new Set<string>();
  private sticky: TickState | null = null;

  constructor(o: TickOptions = {}) {
    this.o = { ...TICK_DEFAULTS, ...o };
  }

  reset() {
    this.seen = [];
    this.seqs.clear();
    this.sticky = null;
  }

  get size() {
    return this.seen.length;
  }

  /**
   * A frame's tick. The same tick right again (the camera caught the frame twice) counts once; the same
   * tick again AFTER a newer one is a loop (kept, so evaluate() can fail it once its signature checks).
   */
  add(k: Tick, seenAt: number) {
    const key = `${k.sid8.join(',')}:${k.seq}:${k.t}`;
    if (this.seqs.has(key)) {
      const sid = k.sid8.join(',');
      const newer = this.seen.some((s) => s.k.sid8.join(',') === sid && s.k.seq > k.seq);
      if (newer) this.seen.push({ k, seenAt, again: true });
      return;
    }
    this.seqs.add(key);
    this.seen.push({ k, seenAt });
    const cut = seenAt - this.o.windowMs;
    while (this.seen.length && this.seen[0].seenAt < cut) this.seen.shift();
    if (this.seen.length > 400) this.seen.splice(0, this.seen.length - 400);
  }

  /** Judges the ticks of `session`. `synced` = the clock offset is known. Failures are sticky until reset(). */
  evaluate(session: { id: Uint8Array; pub: Uint8Array }, synced = true): TickState {
    if (this.sticky) return this.sticky;
    const { min, minSpanMs, driftMs, driftFail, maxFutureMs, staleFail, minRate, maxRate } = this.o;
    const maxAge = synced ? this.o.maxTickAgeMs : Math.max(this.o.maxTickAgeMs, UNSYNCED_MAX_TICK_AGE_MS);
    const mine = this.seen.filter((s) => sameSid8(s.k.sid8, session.id));
    const fresh: Seen[] = [];
    const ages: number[] = [];
    let stale = 0;
    let drifted = 0;
    let youngest = Infinity;
    let maxSeq = -1;
    let last: Tick | undefined;
    for (const s of mine) {
      s.ok ??= verifyTick(s.k, session.id, session.pub);
      if (!s.ok) continue; // not this session's key: ignored (a forged or foreign frame)
      last = s.k;
      const age = s.seenAt - s.k.t;
      ages.push(age);
      if (s.k.seq < maxSeq || (s.again && s.k.seq <= maxSeq)) return this.fail('replay', 'out-of-order', `an older code (tick ${s.k.seq}) came back after tick ${maxSeq}: a recording is looping`, ages, fresh.length, stale, last);
      maxSeq = Math.max(maxSeq, s.k.seq);
      if (age < -maxFutureMs) return this.fail('stale', 'clock', `a tick was signed ${(-age / 1000).toFixed(1)} s in this phone's future: the phone's clock could not be corrected`, ages, fresh.length, stale, last);
      if (age > maxAge) {
        stale++;
        continue;
      }
      fresh.push(s);
      youngest = Math.min(youngest, age);
      if (age - youngest > driftMs && ++drifted >= driftFail) {
        const f0 = fresh[0];
        return this.fail('cadence', 'cadence', `the code's clock moved ${((s.k.t - f0.k.t) / 1000).toFixed(1)} s while this phone's moved ${((s.seenAt - f0.seenAt) / 1000).toFixed(1)} s: a paused, cut or sped-up video`, ages, fresh.length, stale, last);
      }
    }
    if (stale >= staleFail && stale >= fresh.length) {
      const oldest = Math.max(...ages);
      return this.fail('stale', 'stale-tick', `${stale} codes were already ${(oldest / 1000).toFixed(1)} s old when this phone saw them (limit ${(maxAge / 1000).toFixed(1)} s): a recording or a delayed copy`, ages, fresh.length, stale, last);
    }
    const span = fresh.length ? fresh[fresh.length - 1].seenAt - fresh[0].seenAt : 0;
    const tSpan = fresh.length ? Math.max(...fresh.map((f) => f.k.t)) - Math.min(...fresh.map((f) => f.k.t)) : 0;
    if (fresh.length >= min && span >= minSpanMs && tSpan >= minSpanMs * 0.8) {
      const rate = slope(fresh);
      if (rate < minRate || rate > maxRate)
        return this.fail('cadence', 'cadence', `the code's clock ran at ${rate.toFixed(2)}× this phone's: a sped-up or slowed video`, ages, fresh.length, stale, last);
      const worst = Math.max(...fresh.map((s) => s.seenAt - s.k.t));
      return { state: 'live', fresh: fresh.length, stale, min, ages, last, message: `${fresh.length} live ticks over ${(span / 1000).toFixed(1)} s, each at most ${(Math.max(0, worst) / 1000).toFixed(1)} s old.`, detail: `tick age ≤ ${maxAge} ms${synced ? ' (clock synced with PageID)' : ' (clock NOT synced: 30 s fallback)'}, seq rising, drift ≤ ${driftMs} ms` };
    }
    return { state: 'collecting', fresh: fresh.length, stale, min, ages, last, message: `Checking that the code is live: ${fresh.length} of ${min} live ticks`, detail: '' };
  }

  private fail(state: TickState['state'], anomaly: TickAnomaly, detail: string, ages: number[], fresh: number, stale: number, last?: Tick): TickState {
    this.sticky = {
      state,
      fresh,
      stale,
      min: this.o.min,
      ages,
      last,
      anomaly,
      message: state === 'replay' ? 'This looks like a replayed recording, not a live PageID code.' : state === 'cadence' ? 'This code does not move in real time: a paused, cut or sped-up video.' : 'This code is not live: it was shown seconds after it was made, like a recording or a delayed copy.',
      detail,
    };
    return this.sticky;
  }
}

/** The code's clock rate over the phone's: Theil–Sen (the median of pairwise slopes), so one delayed frame does not move it. */
function slope(xs: Seen[]): number {
  const r: number[] = [];
  for (let i = 0; i < xs.length; i++)
    for (let j = i + 1; j < xs.length; j++) {
      const ds = xs[j].seenAt - xs[i].seenAt;
      if (ds >= 250) r.push((xs[j].k.t - xs[i].k.t) / ds);
    }
  if (!r.length) return 1;
  r.sort((a, b) => a - b);
  return r[r.length >> 1];
}
